// Types for the admin AI Settings feature. Must stay in sync with
// tracer/app/models/ai_model_catalog.go + ai_setting.go and the
// ai_settings_controller response shapes.

/** One selectable LLM in the catalog (curated baseline, daily-synced). */
export interface AiModelCatalog {
  id: number;
  publisher: string;
  model_id: string;
  display_name: string;
  versions: string[];
  regions: string[];
  /** AI Ticket / AI Evaluation hard-require this; the UI blocks
   *  selecting a model where it's false. */
  supports_json_schema: boolean;
  modality: string;
  status: string; // "ga" | "preview"
  source: string;
  last_synced_at?: string | null;
  created_at: string;
  updated_at: string;
}

/** The saved singleton row (null until an admin first saves). */
export interface AiSetting {
  id: number;
  engine: string;
  project_id?: string;
  region: string;
  model_id: string;
  model_version?: string;
  timeout_seconds: number;
  github_repo?: string;
  updated_by?: string;
  updated_at: string;
}

/** GitHub issue-integration connection state. */
export interface GithubStatus {
  connected: boolean;
  repo: string;
  /** "token" (admin pasted a token), "env" (legacy
   *  TRACER_GITHUB_TOKEN) or "none". */
  auth_method: 'token' | 'env' | 'none';
  /** GitHub username the stored token belongs to (empty for env-PAT). */
  github_login: string;
  token_present: boolean;
}

export interface GithubStatusResponse {
  github: GithubStatus;
}

/** One selectable repository in the picker. */
export interface GithubRepoOption {
  full_name: string;
  private: boolean;
}

export interface GithubReposResponse {
  repos: GithubRepoOption[];
}

/** Resolved effective config + which tier won (db / env / default). */
export interface EffectiveVertexConfig {
  project_id: string;
  region: string;
  model: string;
  model_version?: string;
  timeout_seconds: number;
  source: 'db' | 'env' | 'default';
}

export interface AiSettingsResponse {
  settings: AiSetting | null;
  effective: EffectiveVertexConfig;
}

export interface AiModelsResponse {
  models: AiModelCatalog[];
}

export interface AiSyncResponse {
  synced: number;
}

export interface AiTestResponse {
  ok: boolean;
  error?: string;
  latency_ms: number;
}

/** Body for PUT /admin/ai/settings. */
export interface AiSettingsBody {
  engine: string;
  project_id?: string;
  region: string;
  model_id: string;
  model_version?: string;
  timeout_seconds: number;
}

// Regions the UI offers, grouped. EU = data stays in the EU (matches
// the DLP/replay-vault posture); picking anything else warns.
export const EU_REGIONS = ['europe-west3', 'europe-west1', 'europe-west4', 'europe-west9'];

export interface RegionOption {
  value: string;
  label: string;
  group: 'eu' | 'multi' | 'non-eu';
}

export const REGION_OPTIONS: RegionOption[] = [
  { value: 'europe-west3', label: 'europe-west3 · Frankfurt', group: 'eu' },
  { value: 'europe-west1', label: 'europe-west1 · Belgium', group: 'eu' },
  { value: 'europe-west4', label: 'europe-west4 · Netherlands', group: 'eu' },
  { value: 'europe-west9', label: 'europe-west9 · Paris', group: 'eu' },
  { value: 'global', label: 'global · best availability, no region guarantee', group: 'multi' },
  { value: 'us-central1', label: 'us-central1 · Iowa', group: 'non-eu' },
  { value: 'us-east5', label: 'us-east5 · Columbus', group: 'non-eu' },
  { value: 'asia-northeast1', label: 'asia-northeast1 · Tokyo', group: 'non-eu' },
];
